/**
 * DLP の概要 `/dlp/overview`。KPI(端末・スキャン・検出・USB)+ 直近スキャン + 直近 USB。
 * 数字は `GET /api/dlp/overview`、表は各一覧 API の先頭 5 件。契約に無い機能の表は出さない(403 を避ける)。
 */
import { Alert, Card, Col, Row, Statistic, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Link } from "react-router-dom";

import type { DlpScan, DlpUsbEvent } from "../../lib/api";
import { PageHeader, TimeAgo } from "../../ui";
import { bytesText, deviceCell, employeeCell, scanStatusTag, usbKindTag, usbModeTag } from "./bits";
import { errorText, useDlpOverview, useDlpPerms, useDlpScans, useDlpUsbEvents } from "./hooks";

const { Text } = Typography;
/** 一覧 API は `limit` / `offset`(core の pagination)。page / page_size は無視されるので使わない */
const RECENT = { limit: "5", offset: "0" };

const SCAN_COLUMNS: ColumnsType<DlpScan> = [
  { title: "#", dataIndex: "id", width: 110, render: (v: number) => <Link to={`/dlp/scans?panel=${v}`} className="ui-num">{v}</Link> },
  { title: "端末", width: 180, render: (_: unknown, r) => deviceCell(r.device) },
  { title: "従業員", width: 180, render: (_: unknown, r) => employeeCell(r.employee) },
  { title: "状態", width: 110, render: (_: unknown, r) => scanStatusTag(r) },
  { title: "検出", dataIndex: "findings_count", width: 90, render: (v: number) => <span className="ui-num">{v}</span> },
  { title: "依頼", dataIndex: "created_at", width: 160, render: (v: string) => <TimeAgo iso={v} /> },
];

type TopDomain = { rule_domain: string; bytes: number; devices: number };

const TOP_DOMAIN_COLUMNS: ColumnsType<TopDomain> = [
  {
    title: "宛先",
    width: 240,
    dataIndex: "rule_domain",
    render: (v: string) => (
      <Link to={`/dlp/upload?q=${encodeURIComponent(v)}`} className="ui-num">
        {v}
      </Link>
    ),
  },
  { title: "推定送信量", dataIndex: "bytes", width: 160, render: (v: number) => <span className="ui-num">{bytesText(v)}</span> },
  { title: "端末", dataIndex: "devices", width: 160, render: (v: number) => <span className="ui-num">{v} 台</span> },
];

const USB_COLUMNS: ColumnsType<DlpUsbEvent> = [
  { title: "日時", dataIndex: "at", width: 160, render: (v: string) => <TimeAgo iso={v} /> },
  { title: "種別", width: 110, render: (_: unknown, r) => usbKindTag(r.kind) },
  { title: "端末", width: 180, render: (_: unknown, r) => deviceCell(r.device) },
  { title: "ドライブ", width: 160, render: (_: unknown, r) => [r.drive, r.label].filter(Boolean).join(" ") },
  { title: "ユーザー", dataIndex: "username", width: 160 },
];

export function OverviewPage() {
  const { hasExitScan, hasUsb } = useDlpPerms();
  const overview = useDlpOverview();
  const scans = useDlpScans(RECENT, hasExitScan);
  const usb = useDlpUsbEvents(RECENT, hasUsb);
  const o = overview.data;
  const up = o?.upload ?? null;
  const active = o ? o.scans.queued + o.scans.sent + o.scans.running : undefined;
  const toReview = o?.scans.done;

  return (
    <div className="ui-list">
      <div className="ui-list-main">
        <PageHeader
          title="概要"
          description="退職時スキャン・USB 利用ログ・アップロード検知の現在地。端末はエージェントの poll とデバイス管理の割当イベントから集まる。"
        />
        {overview.isError && (
          <Alert type="error" showIcon message="概要を取得できなかった" description={errorText(overview.error)} />
        )}
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="端末" value={o?.devices.total ?? "—"} suffix="台" loading={overview.isLoading} />
              <Text type="secondary">24 時間以内に poll: {o?.devices.online_24h ?? "—"} 台</Text>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="進行中のスキャン" value={active ?? "—"} suffix="件" loading={overview.isLoading} />
              <Text type="secondary">
                確認待ち(完了): <Link to="/dlp/scans?status=done">{toReview ?? "—"} 件</Link> / 失敗: {o?.scans.error ?? "—"} 件
              </Text>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="検出(30 日)" value={o?.findings_30d ?? "—"} suffix="件" loading={overview.isLoading} />
              <Text type="secondary">確認済み: {o?.scans.reviewed ?? "—"} 件</Text>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="USB イベント(7 日)" value={o?.usb_events_7d ?? "—"} suffix="件" loading={overview.isLoading} />
              <Text type="secondary">既定の USB モード: {o ? usbModeTag(o.usb_mode_default) : "—"}</Text>
            </Card>
          </Col>
        </Row>

        {up && (
          <>
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Statistic title="アップロード検知(7 日)" value={up.detected_7d} suffix="件" />
                  <Text type="secondary">
                    うち推定(共有 IP):{" "}
                    <Link to="/dlp/upload?kind=detected&attribution=ambiguous">{up.ambiguous_7d} 件</Link>
                  </Text>
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Statistic title="宛先不明の大量送信(7 日)" value={up.unknown_bulk_7d} suffix="件" />
                  <Text type="secondary">
                    <Link to="/dlp/upload?kind=unknown_bulk">一覧で見る</Link>
                  </Text>
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Statistic title="観測中の端末" value={up.devices_observing} suffix="台" />
                  <Text type="secondary">エラー: {up.devices_error} 台</Text>
                </Card>
              </Col>
            </Row>
            <Card
              size="small"
              title="送信の多い宛先(7 日、推定)"
              extra={<Link to="/dlp/upload">すべて</Link>}
              style={{ marginBottom: 16 }}
            >
              <Table<TopDomain>
                scroll={{ x: "max-content" }}
                size="small"
                rowKey="rule_domain"
                columns={TOP_DOMAIN_COLUMNS}
                dataSource={up.top_domains_7d}
                pagination={false}
                locale={{ emptyText: "検知はまだない" }}
              />
            </Card>
          </>
        )}

        {hasExitScan && (
          <Card
            size="small"
            title="直近のスキャン"
            extra={<Link to="/dlp/scans">すべて</Link>}
            style={{ marginBottom: 16 }}
          >
            {scans.isError && <Alert type="error" showIcon message={errorText(scans.error)} />}
            <Table<DlpScan>
              scroll={{ x: "max-content" }}
              size="small"
              rowKey="id"
              columns={SCAN_COLUMNS}
              dataSource={scans.data?.items ?? []}
              loading={scans.isLoading}
              pagination={false}
              locale={{ emptyText: "スキャンはまだない" }}
            />
          </Card>
        )}

        {hasUsb && (
          <Card size="small" title="直近の USB イベント" extra={<Link to="/dlp/usb">すべて</Link>}>
            {usb.isError && <Alert type="error" showIcon message={errorText(usb.error)} />}
            <Table<DlpUsbEvent>
              scroll={{ x: "max-content" }}
              size="small"
              rowKey="id"
              columns={USB_COLUMNS}
              dataSource={usb.data?.items ?? []}
              loading={usb.isLoading}
              pagination={false}
              locale={{ emptyText: "USB イベントはまだない" }}
            />
          </Card>
        )}
      </div>
    </div>
  );
}
